import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import { useCollectionsStore } from './collections.js';

const { VITE_apiUrl: apiUrl } = import.meta.env;

// How long to trust persisted data before re-validating with the server (5 minutes)
const CACHE_MAX_AGE = 5 * 60 * 1000;

export const useUserStore = defineStore('user', {
  state: () => ({
    isLoggedIn: false,
    user: null,
    role: null,
    permissions: null,
    pauseConnectivityChecks: false,
    lastFetched: null
  }),
  persist: {
    paths: ['isLoggedIn', 'user', 'role', 'permissions', 'lastFetched']
  },
  actions: {
    async fetchUserData() {
      const response = await fetch(`${apiUrl}/users/me`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user data');
      }

      const result = await response.json();
      this.user = result.data;

      if (this.user.role) {
        const roleResponse = await fetch(`${apiUrl}/roles/${this.user.role}?fields[]=id`, {
          credentials: 'include'
        });
        if (roleResponse.ok) {
          const roleResult = await roleResponse.json();
          this.role = roleResult.data;
        }

        const permResponse = await fetch(`${apiUrl}/permissions/me`, {
          credentials: 'include'
        });
        if (permResponse.ok) {
          const permResult = await permResponse.json();
          this.permissions = permResult.data;
        }
      }

      this.isLoggedIn = true;
      this.lastFetched = Date.now();
    },

    async checkAuth() {
      // If we have persisted data and it's fresh enough, skip the network call
      if (this.isLoggedIn && this.user && this.lastFetched) {
        const age = Date.now() - this.lastFetched;
        if (age < CACHE_MAX_AGE) {
          // Set Sentry user from cache if not already set
          Sentry.setUser({
            email: this.user.email,
            username: this.user.first_name + " " + this.user.last_name,
          });
          return;
        }
      }

      try {
        const response = await fetch(`${apiUrl}/users/me`, {
          credentials: 'include'
        });

        if (response.ok) {
          if (!this.isLoggedIn || !this.lastFetched || (Date.now() - this.lastFetched) > CACHE_MAX_AGE) {
            await this.fetchUserData();
          }
          Sentry.setUser({
            email: this.user.email,
            username: this.user.first_name + " " + this.user.last_name,
          });
        } else {
          this.logout();
        }
      } catch (error) {
        this.logout();
      }
    },

    async logout() {
      try {
        await fetch(`${apiUrl}/auth/logout`, {
          method: 'POST',
          credentials: 'include'
        });
      } catch (e) {
        // Ignore logout errors
      }

      this.isLoggedIn = false;
      this.user = null;
      this.role = null;
      this.permissions = null;
      this.lastFetched = null;
      Sentry.setUser(null);
      // Clear all collection caches on logout
      try { useCollectionsStore().clearAll(); } catch (e) { /* store may not be initialized */ }
    }
  },
});
